/**
 * Cosmetic position interpolation + distance-driven walk/idle animation,
 * extracted verbatim from the game client's frame loop (game.ts `frame`) so the
 * game and any other authoritative renderer (the arena) share ONE
 * implementation — same code ⇒ the hero moves and animates identically in
 * both, and they can't drift apart.
 *
 * This is a pure function of the entity lists + the interpolation state +
 * wall-clock `now`/`dtMs` supplied by the caller (the client is a renderer; it
 * may use wall-clock for cosmetics — the sim never sees it, AGENTS §6). No DOM.
 *
 * The model (unchanged from the game): a 1-tile step spans the MOVER's own
 * cadence — the sim's per-tick cadence for that entity (the hero's
 * MOVE_COST_TICKS ticks, or an enemy's authored `moveSpeedTicks` stat, both
 * × TICK_MS) — and the interpolant is anchored to the step START,
 * not the 50 ms tick burst — consecutive observations are identical between
 * steps, so a per-tick lerp would lurch the whole tile in one tick and stand
 * still for the rest. Mid-walk retargets continue from the current render
 * position (the sprite turns instead of snapping back a tile). A multi-tile
 * delta (teleport / doorway jump) snaps with no walk anim.
 *
 * Two feel rules ride on top of that model, both because the authoritative
 * position is a STEP function rendered at ~60 fps: a new step is back-dated by
 * the age of the packet that revealed it (so the frame a step is noticed on
 * already moves), and a stride pose COASTS across a momentary stationary frame
 * (IDLE_HOLD_MS) instead of flashing the idle stance and restarting the cycle.
 * Without them the hero visibly hitched — and flickered walk/idle — once per
 * tile step.
 */
import { TICK_MS } from "@pd/shared";
import type { Observation, ObservedAttack } from "@pd/shared";
import { dirForDelta, stepPlayerAnim } from "./hero.js";
import type { PlayerAnim } from "./hero.js";
import type { Vec2 } from "./actions.js";

/**
 * Namespaced interpolation keys. A player and an enemy can share the same
 * numeric id (the arena's hero + goblin are both id 0 — they live in separate
 * obs arrays, but the interpolation's position/anim maps are one flat map), so
 * key them by namespace or one silently overwrites the other and the hero
 * renders on top of the enemy.
 */
export const playerKey = (id: number): string => `p${id}`;
export const enemyKey = (id: number): string => `e${id}`;

/** A moving thing to interpolate — a player (game) or hero/enemy (arena). */
export interface MovingEntity {
  /** Namespace-unique key (playerKey/enemyKey), not the raw id. */
  id: string;
  x: number;
  y: number;
  /** This mover's per-tile render duration in ms: the cadence the SIM steps
   *  the mover at (an enemy's `moveSpeedTicks` × TICK_MS, or the hero's
   *  shared MOVE_COST_TICKS × TICK_MS). A 50-tick dragon walks 2500 ms per
   *  tile; lerping a slow mover over the fixed 350 ms hero step makes it sit
   *  still for the remaining ~2.1 s before the sim's next step. */
  stepMs: number;
  /** True for movers the sim may FORCE-MOVE across several tiles in one step
   *  (an enemy a pull ability — Black Hole — dragged): a multi-tile delta
   *  then renders as a short "sucked in" slide toward the new tile instead
   *  of a teleport snap. Players are never marked: their only multi-tile
   *  jumps are doorway teleports, which must snap. */
  draggable?: boolean;
  /** An authoritative teleport identity, including one-tile escapes. */
  teleportTick?: number;
}

/** Mutable interpolation state, owned by the caller (one per viewport). */
export interface InterpState {
  /** In-flight step per entity: from → to, anchored in wall-clock at `start`
   *  over `ms` (a forced multi-tile drag sets its own duration). */
  steps: Map<string, { from: Vec2; to: Vec2; start: number; ms?: number }>;
  /** Previous frame's lerped positions (the distance source + retarget `from`). */
  lastLerped: Map<string, Vec2>;
  /** Per-entity walk/idle animation state (distance-driven). */
  anims: Map<string, PlayerAnim>;
  /** How long each mover has been rendering WITHOUT moving (ms). The stride
   *  pose coasts while this stays under IDLE_HOLD_MS, so one stationary frame
   *  never pops the idle stance (see IDLE_HOLD_MS). */
  stillMs: Map<string, number>;
}

export function createInterpState(): InterpState {
  return { steps: new Map(), lastLerped: new Map(), anims: new Map(), stillMs: new Map() };
}

/**
 * How long a mover may hold its stride pose while the RENDER position is
 * momentarily stationary (ms).
 *
 * The authoritative position is a step function, and a new position is only
 * noticed on the frame after the packet lands, so a continuously walking hero
 * gets a stationary render frame at every step boundary. `stepPlayerAnim` reads
 * any stationary frame as "stopped" (walkDist 0 → the IDLE stance, a different
 * pose from the stride), which flashed the idle frame for one render frame per
 * tile step and restarted the walk cycle from frame 0 — a visible walk/idle
 * flicker about 3× a second at the hero's cadence, and (since one 6-frame
 * stride spans 2 tiles) a stride that never got past frames 0–2.
 *
 * So a stationary frame inside this window COASTS: the stride holds and the
 * idle clock stays reset. Only a mover stopped for longer than the window drops
 * to idle. Sized for a dropped frame plus packet jitter at any sane frame rate,
 * and short enough that a real stop still reads as immediate.
 */
export const IDLE_HOLD_MS = 90;

export interface InterpResult {
  /** Lerped tile positions for this frame, keyed by entity key (p0/e0). */
  positions: Map<string, Vec2>;
  /** The animation state (the same map the caller stashed on `state.anims`). */
  anims: Map<string, PlayerAnim>;
}

const DEFAULT_ANIM: PlayerAnim = { dir: "s", walkDist: 0, idleT: 0 };

/**
 * Advance the interpolation by one render frame. `entities` is the current
 * authoritative position of every moving thing; `prevEntities` the previous
 * frame's (null on the first observation). Mutates `state`; returns the
 * positions to draw + the (mutated) anims map.
 *
 * `stepAgeMs` is how long BEFORE this frame the current authoritative
 * observation was received. A new step is back-dated by it (capped at one
 * frame): the sim moved the mover when the packet arrived, not on the frame
 * that noticed, and anchoring at `now` spent the detection frame standing still
 * — a stutter at the start of every step.
 */
export function stepInterpolation(
  state: InterpState,
  entities: MovingEntity[],
  prevEntities: MovingEntity[] | null,
  now: number,
  dtMs: number,
  stepAgeMs = 0,
): InterpResult {
  const prevById = new Map<string, MovingEntity>();
  if (prevEntities) for (const e of prevEntities) prevById.set(e.id, e);
  const backdate = Math.max(0, Math.min(stepAgeMs, dtMs));

  const positions = new Map<string, Vec2>();
  for (const p of entities) {
    const prevP = prevById.get(p.id) ?? null;
    const last = state.steps.get(p.id) ?? null;
    const ddx = prevP ? p.x - prevP.x : 0;
    const ddy = prevP ? p.y - prevP.y : 0;
    let pos: Vec2;
    const teleported = !!prevP && p.teleportTick !== undefined && prevP.teleportTick !== p.teleportTick;
    if (teleported) {
      pos = { x: p.x, y: p.y };
      state.steps.set(p.id, { from: pos, to: pos, start: now });
    } else if (prevP && last && last.to.x === p.x && last.to.y === p.y) {
      // Same step continues — keep the interpolant anchored to its start,
      // over the MOVER's own cadence (a slow mover glides its whole step);
      // a forced drag carries its own shorter duration.
      const t = Math.min(1, (now - last.start) / (last.ms ?? p.stepMs));
      pos = { x: last.from.x + (p.x - last.from.x) * t, y: last.from.y + (p.y - last.from.y) * t };
    } else if (ddx !== 0 || ddy !== 0) {
      if (Math.abs(ddx) > 1 || Math.abs(ddy) > 1) {
        if (p.draggable) {
          // A FORCED multi-tile move (a pull): drag the sprite over a short
          // slide — a few hundred ms, scaled a little with distance — so it
          // reads as "sucked in" rather than a teleport. Purely cosmetic;
          // the sim already moved it (AGENTS §6).
          const from = state.lastLerped.get(p.id) ?? { x: prevP!.x, y: prevP!.y };
          const dist = Math.max(Math.abs(p.x - from.x), Math.abs(p.y - from.y));
          const ms = Math.min(600, Math.max(120, dist * 150));
          state.steps.set(p.id, { from, to: { x: p.x, y: p.y }, start: now, ms });
          pos = from;
        } else {
          // Teleport / doorway jump: snap, no walk anim.
          pos = { x: p.x, y: p.y };
          state.steps.set(p.id, { from: pos, to: { x: pos.x, y: pos.y }, start: now });
        }
      } else {
        // New 1-tile step. On a mid-walk retarget, continue from the current
        // render position so the sprite turns instead of snapping backward.
        // The step is anchored a packet-age in the PAST and this frame is
        // resolved against that anchor, so the frame the step is noticed on
        // already advances — returning `from` here froze the mover for a frame
        // at every step boundary.
        const from = state.lastLerped.get(p.id) ?? { x: prevP!.x, y: prevP!.y };
        const start = now - backdate;
        state.steps.set(p.id, { from, to: { x: p.x, y: p.y }, start });
        const t0 = Math.max(0, Math.min(1, (now - start) / p.stepMs));
        pos = t0 > 0
          ? { x: from.x + (p.x - from.x) * t0, y: from.y + (p.y - from.y) * t0 }
          : from;
      }
    } else {
      // Stationary (or first observation): settle on the observed tile.
      pos = { x: p.x, y: p.y };
      state.steps.set(p.id, { from: pos, to: { x: pos.x, y: pos.y }, start: now });
    }
    positions.set(p.id, pos);

    // Animation: distance-driven walk while the render position moves, idle
    // otherwise. Teleport snaps land on idle immediately.
    const prevLerped = state.lastLerped.get(p.id);
    const moved = prevLerped ? Math.hypot(pos.x - prevLerped.x, pos.y - prevLerped.y) : 0;
    const prevAnim = state.anims.get(p.id) ?? DEFAULT_ANIM;
    // A dragged mover is NOT a snap: it keeps facing the pull and idles
    // through the slide (a dragged corpse doesn't walk). A true snap
    // (doorway teleport) resets the anim.
    const snapped = teleported || ((Math.abs(ddx) > 1 || Math.abs(ddy) > 1) && !p.draggable);
    // The stride coast (see IDLE_HOLD_MS): hold the walk pose across a short
    // stationary gap instead of popping to idle and restarting the cycle.
    const still = moved > 0 ? 0 : (state.stillMs.get(p.id) ?? 0) + dtMs;
    const coasting = !snapped && moved === 0 && still > 0 && still <= IDLE_HOLD_MS && prevAnim.walkDist > 0;
    state.stillMs.set(p.id, still);
    state.anims.set(
      p.id,
      snapped
        ? { dir: prevAnim.dir, walkDist: 0, idleT: 0 }
        : coasting
          ? { ...prevAnim, dir: dirForDelta(ddx, ddy) ?? prevAnim.dir }
          : stepPlayerAnim(prevAnim, dtMs, moved, { dx: ddx, dy: ddy }),
    );
  }
  for (const id of [...state.anims.keys()]) {
    if (!entities.some((e) => e.id === id)) {
      state.anims.delete(id);
      state.stillMs.delete(id);
    }
  }
  state.lastLerped = positions;
  return { positions, anims: state.anims };
}

/** The swing anim's frame count (the part/NPC pipeline's 6-frame attack). */
export const SWING_FRAMES = 6;
/** The attack anim's natural playback rate (atlas canon: ANIM_FPS["attack"] in
 *  the hero + NPC pipeline build_atlas; committed as anims.attack.fps). */
export const ATTACK_FPS = 14;
/** The attack anim's strike frame — the blade-down "active_frame" in the
 *  pipeline specs (tools/heropipeline/spec/rig.json, tools/npcpipeline/spec/
 *  goblin.json). It is anchored to the swing's impactTick. */
export const ATTACK_ACTIVE_FRAME = 4;

/**
 * Overlay the authoritative swing onto the walk/idle anims: for each entity
 * (player or enemy) with an in-flight attack, the 6-frame attack anim plays at
 * its NATURAL rate (ATTACK_FPS) with the strike frame (ATTACK_ACTIVE_FRAME)
 * spanning impactTick — the blade is down exactly when the authoritative hit
 * lands — so a combatant reads as "kinda idle" for most of its swing cadence,
 * then does a quick slash. Before the windup starts and after the six frames
 * finish (the swing window may still be open) no attackFrame is set, and the
 * renderer falls back to walk/idle. Facing the target holds for the whole
 * swing window. Pure over `obs` + `tick` (tick-quantized at 20 tps — identical
 * on every client); mutates `anims` (the fresh per-frame objects
 * stepInterpolation just produced, so the frame is re-derived — and cleared —
 * every frame). The hit already resolved server-side at impactTick; this is
 * anim timing only, never a predicted outcome.
 */
export function applyCombatAnims(anims: Map<string, PlayerAnim>, obs: Observation, tick: number): void {
  // Coasting can retain last frame's animation object: clear overlays even
  // when the server has just cleared the attack.
  for (const anim of anims.values()) { delete anim.attackFrame; delete anim.heavyWindup; delete anim.pounceFrame; delete anim.pounceHeight; delete anim.pounceWindup; delete anim.pounceElement; delete anim.summonFrame; delete anim.meteorWindup; }
  const targetPos = (targetId: number, targetIsEnemy: boolean): { x: number; y: number } | null => {
    const t = targetIsEnemy
      ? obs.enemies.find((e) => e.id === targetId)
      : obs.players.find((p) => p.id === targetId);
    return t ? { x: t.x, y: t.y } : null;
  };
  const apply = (key: string, entity: {
    x: number;
    y: number;
    attack: ObservedAttack | null;
  }): void => {
    if (!entity.attack) return;
    const a = entity.attack;
    const anim = anims.get(key);
    if (anim) {
      // Impact-anchored, natural-fps frame. tMs is signed ms relative to the
      // impact tick: the windup (frames 0..ACTIVE-1) plays in the ticks before
      // it, the strike frame spans [0, frameMs), the follow-through after. A
      // swing shorter than the windup needs no special case — the lower window
      // bound clamps the start mid-anim, and the strike still lands on
      // impactTick. Outside the window (idle lead-in / post-anim tail) the
      // field stays unset: walk/idle renders.
      const frameMs = 1000 / ATTACK_FPS;
      const tMs = (tick - a.impactTick) * TICK_MS;
      if (a.bossSpecial) {
        const windup = Math.max(1, a.impactTick - a.startTick);
        if (tick < a.impactTick) {
          anim.heavyWindup = Math.max(0, Math.min(1, (tick - a.startTick) / windup));
          // Raise at normal speed, hold the cocked club, then release quickly.
          anim.attackFrame = Math.max(0, Math.min(ATTACK_ACTIVE_FRAME - 1, Math.floor((tick - a.startTick) * TICK_MS / frameMs)));
        } else if (tMs < (SWING_FRAMES - ATTACK_ACTIVE_FRAME) * frameMs) {
          anim.attackFrame = Math.min(SWING_FRAMES - 1, ATTACK_ACTIVE_FRAME + Math.floor(tMs / frameMs));
        }
      } else if (tMs >= -ATTACK_ACTIVE_FRAME * frameMs && tMs < (SWING_FRAMES - ATTACK_ACTIVE_FRAME) * frameMs) {
        anim.attackFrame = Math.max(0, Math.min(SWING_FRAMES - 1, Math.floor(tMs / frameMs) + ATTACK_ACTIVE_FRAME));
      }
      // Face the target for the whole swing window (pre-anim idle included).
      const target = targetPos(a.targetId, a.targetIsEnemy);
      if (target) {
        const dir = dirForDelta(target.x - entity.x, target.y - entity.y);
        if (dir) anim.dir = dir;
      }
    }
  };
  for (const p of obs.players) apply(playerKey(p.id), p);
  for (const e of obs.enemies) apply(enemyKey(e.id), e);
  for (const e of obs.enemies) {
    const shower = e.meteorShower;
    const anim = anims.get(enemyKey(e.id));
    if (!e.alive || !shower || !anim || tick < shower.startTick || tick >= shower.endTick) continue;
    anim.walkDist = 0;
    if (tick < shower.impactTick) {
      anim.meteorWindup = (tick - shower.startTick) / Math.max(1, shower.impactTick - shower.startTick);
      anim.summonFrame = Math.min(2, Math.floor((tick - shower.startTick) * TICK_MS / (1000 / 6)));
    } else {
      anim.summonFrame = Math.max(0, 2 - Math.floor((tick - shower.impactTick) * 3 / Math.max(1, shower.endTick - shower.impactTick)));
    }
  }
  for (const e of obs.enemies) {
    if (!e.freezeBlink) continue;
    const anim = anims.get(enemyKey(e.id));
    if (!anim) continue;
    const cast = e.freezeBlink;
    const progress = Math.max(0, Math.min(1, (tick - cast.startTick) / Math.max(1, cast.endTick - cast.startTick)));
    anim.heavyWindup = progress;
    anim.attackFrame = Math.min(3, Math.floor(progress * 4));
    anim.walkDist = 0;
    const target = targetPos(cast.targetId, false);
    if (target) anim.dir = dirForDelta(target.x - e.x, target.y - e.y) ?? anim.dir;
  }
}
